/**
 * @file noCopyPaste.ts
 * @description
 * Shared helpers that block clipboard interaction on sensitive fields
 * (passwords). Every masked input in the app routes through these so the
 * behaviour is identical on web and native.
 *
 * Web: react-native-web renders a real <input>, so we attach the DOM clipboard
 * listeners directly to the node and preventDefault them. Covers the keyboard
 * shortcuts, the right-click menu and drag-and-drop of text into the field.
 * Native: `contextMenuHidden` removes the long-press Cut/Copy/Paste menu.
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

// Events that would move text into or out of the field.
const BLOCKED_EVENTS = ['copy', 'cut', 'paste', 'drop', 'dragstart', 'contextmenu'];

/**
 * Returns a ref to hand to a TextInput. While `enabled`, all clipboard and
 * drag events on the underlying web input are swallowed.
 */
export function useBlockCopyPaste<T = any>(enabled: boolean) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    // Only the web build has a DOM node to attach listeners to.
    if (!enabled || Platform.OS !== 'web') return;

    const node = ref.current as any;
    if (!node?.addEventListener) return;

    const block = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    BLOCKED_EVENTS.forEach((name) => node.addEventListener(name, block));
    return () => BLOCKED_EVENTS.forEach((name) => node.removeEventListener(name, block));
  }, [enabled]);

  return ref;
}

/**
 * TextInput props that suppress the native long-press Cut/Copy/Paste menu and
 * keep autofill/suggestion surfaces away from the value.
 */
export function noCopyPasteProps(enabled: boolean) {
  if (!enabled) return {};
  return {
    contextMenuHidden: true,
    autoCorrect: false,
    spellCheck: false,
    // Android's suggestion strip can echo typed characters back; off for secrets.
    autoComplete: 'off' as const,
  };
}
